import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ScenarioMessageTypes } from "../../enums/ScenarioMessageTypes.enum";
import { useSocket } from "../../context/SocketContext";
import {
	assignableUsers,
	clockOf,
	EntryRow,
	nameOf,
	ProtocolPhase,
	ProtocolRow,
} from "../../utils/protocolForm";
import ProtocolEntryRow from "./ProtocolEntryRow";
import ProtocolNewEntryRow from "./ProtocolNewEntryRow";
import { useProtocolText } from "./useProtocolText";

interface ProtocolFormProps {
	rows: ProtocolRow[];
	/** Rows before filtering, to tell "no entries yet" from "none in this filter". */
	totalRows: number;
	canKeep: boolean;
	currentPhaseId: number;
	phases: ProtocolPhase[];
	exerciseName: string;
}

/** The phase's label: "Phase 3 · Eskalation", or "Phase 3" when it has no name. */
export function usePhaseLabel(phases: ProtocolPhase[]) {
	const tf = useProtocolText();

	return (id?: number) => {
		const name = phases.find((phase) => phase.id === id)?.phaseName?.trim();

		return name ? tf("phaseWithName", { id, name }) : tf("phase", { id });
	};
}

/**
 * The protocol of a running scenario as the paper form "Anlage 3 – Vordruck
 * Ereignisdokumentation" (BCM360-376): one table, every row alike whoever wrote it.
 */
export default function ProtocolForm({
	rows,
	totalRows,
	canKeep,
	currentPhaseId,
	phases,
	exerciseName,
}: ProtocolFormProps) {
	const tf = useProtocolText();
	const { i18n } = useTranslation();
	const phaseLabel = usePhaseLabel(phases);
	const { runUsers, errInfo, sendProtocolMessage, updateProtocolMessage, updateProtocolTask } =
		useSocket();
	const sheetRef = useRef<HTMLDivElement>(null);
	const previousRows = useRef(0);

	const userById = useMemo(() => {
		const byId = new Map(runUsers.map((user) => [String(user.id), user]));

		return (id?: string | null) => (id ? byId.get(String(id)) : undefined);
	}, [runUsers]);
	const assignable = useMemo(() => assignableUsers(runUsers), [runUsers]);

	// The leader's run entry is written when the run starts; it dates the form.
	const leaderEntry = runUsers.find(
		(user) => !!errInfo?.email && user.email?.toLowerCase() === errInfo.email.toLowerCase()
	);
	const [openedAt] = useState(() => Date.now());
	const startedAt = leaderEntry?.firstJoinTimeStamp ?? openedAt;
	const keptBy = nameOf(errInfo) || errInfo?.email || "";

	// A new row scrolls into view, as the protocol always has.
	useEffect(() => {
		if (sheetRef.current && rows.length > previousRows.current) {
			sheetRef.current.scrollTop = sheetRef.current.scrollHeight;
		}

		previousRows.current = rows.length;
	}, [rows.length]);

	const sysCell = "border border-border px-3 py-1.5 text-xs font-semibold tracking-wide";

	return (
		<div className="flex min-h-0 flex-1 flex-col" data-testid="protocol-form">
			<div className="flex flex-none flex-wrap items-baseline gap-x-7 gap-y-1 px-5 pb-2.5 pt-3 text-[13px] text-subtle">
				<span className="mr-auto text-[15px] font-bold text-ink">{tf("title")}</span>
				<span>
					{tf("date")}:{" "}
					<b className="font-semibold text-text">
						{new Date(startedAt).toLocaleDateString(
							i18n.language?.startsWith("de") ? "de-DE" : "en-GB"
						)}
					</b>
				</span>
				<span>
					{tf("exercise")}: <b className="font-semibold text-text">{exerciseName}</b>
				</span>
				<span>
					{tf("keptBy")}: <b className="font-semibold text-text">{keptBy}</b>
				</span>
			</div>
			<div ref={sheetRef} className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-5">
				<table className="w-full table-fixed border-collapse">
					<colgroup>
						<col />
						<col className="w-[84px]" />
						<col className="w-[200px]" />
						<col className="w-[150px]" />
					</colgroup>
					<thead>
						<tr>
							{["record", "time", "assignee", "done"].map((column) => (
								<th
									key={column}
									className="sticky top-0 z-[7] border border-border bg-surface px-3 py-2 text-left text-xs font-bold leading-tight text-neutral-700"
								>
									{tf(`columns.${column}`)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => {
							if (row.kind === "phase") {
								return (
									<tr key={row.key} data-row="phase">
										<td className={`${sysCell} bg-surface text-subtle`} colSpan={4}>
											{phaseLabel(row.phaseId)}
											<span className="float-right font-normal tabular-nums">
												{tf("clock", { time: clockOf(row.at) })}
											</span>
										</td>
									</tr>
								);
							}

							if (row.kind === "time") {
								const leftTime = row.message.data?.descriptionOptions?.leftTime;

								return (
									<tr key={row.key} data-row="time">
										<td className={`${sysCell} bg-warning-light text-warning-ink`} colSpan={4}>
											{leftTime
												? tf("timeWarning", { count: Number(leftTime) })
												: tf("timeWarningEmpty")}
											<span className="float-right font-normal tabular-nums">
												{tf("clock", { time: clockOf(row.at) })}
											</span>
										</td>
									</tr>
								);
							}

							if (row.kind === "end") {
								return (
									<tr key={row.key} data-row="end">
										<td className={`${sysCell} bg-surface text-subtle`} colSpan={4}>
											{tf("end")}
											<span className="float-right font-normal tabular-nums">
												{tf("clock", { time: clockOf(row.at) })}
											</span>
										</td>
									</tr>
								);
							}

							return (
								<ProtocolEntryRow
									key={row.key}
									assignable={assignable}
									canKeep={canKeep}
									row={row as EntryRow}
									userById={userById}
									onAssign={(messageId, assigneeId) =>
										updateProtocolTask(messageId, { assigneeId })
									}
									onEdit={(messageId, message, recordedAt) =>
										updateProtocolMessage(ScenarioMessageTypes.MESSAGE, messageId, {
											message,
											recordedAt,
										})
									}
									onToggleDone={(messageId, done) => updateProtocolTask(messageId, { done })}
								/>
							);
						})}
						{!rows.length && (
							<tr>
								<td
									className="border border-border px-3 py-5 text-center text-sm text-muted"
									colSpan={4}
								>
									{totalRows ? tf("emptyFilter") : tf("empty")}
								</td>
							</tr>
						)}
					</tbody>
					{canKeep && (
						<tbody>
							<ProtocolNewEntryRow
								assignable={assignable}
								currentPhaseId={currentPhaseId}
								onAdd={(entry) => sendProtocolMessage(ScenarioMessageTypes.MESSAGE, entry)}
							/>
						</tbody>
					)}
				</table>
				<div className="h-3" />
			</div>
		</div>
	);
}
